import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus, Label, Separator, Switch} from 'platform/components';
import {
  Box,
  Grid,
  HStack,
  Icon,
  Integer,
  Show,
  Space,
  Text,
  ValueByDevice,
  VStack,
} from 'platform/foundation';

import {isTrue} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {BlacklistFlag} from '../../../../../../components/HitlistBlacklistFlag/BlacklistFlag';
import {HitlistFlag} from '../../../../../../components/HitlistBlacklistFlag/HitlistFlag';
import {SourcingType, useSourcingType} from '../../../../../../hooks/useSourcingType';
import {useMakeModelFilter} from '../../../../hooks/useMakeModelFilter';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {FilterVehicleCategory} from '../FilterVehicleCategory/FilterVehicleCategory';
import {MakeCheckboxList} from './components/MakeCheckboxList';
import {MakesModelsSearch} from './components/MakesModelsSearch';
import {ModelFamiliesCheckboxTree} from './components/ModelFamiliesCheckboxTree';
import {TopMakes} from './components/TopMakes';
import {TopSellingCheckboxList} from './components/TopSellingCheckboxList';

const FILTERS_GRID_MAX_HEIGHT_DEFAULT = 150;
/** The three panes stack below the notebook breakpoint, so full height means ~1800px of scroll. */
const FILTERS_GRID_MAX_HEIGHT_MOBILE = 40;
const FILTERS_GRID_MAX_HEIGHT: ValueByDevice<Integer> = [
  FILTERS_GRID_MAX_HEIGHT_MOBILE,
  FILTERS_GRID_MAX_HEIGHT_MOBILE,
  FILTERS_GRID_MAX_HEIGHT_DEFAULT,
];

export function FilterMakesModels(props: TestIdProps) {
  const {filters, onUpdateFilters} = useFilters();
  const {isLoading, isError} = useMakeModelFilter();
  const sourcingType = useSourcingType();
  // The category is classifieds-only: `all_auctions` silently ignores `vehicle_type`
  const isVehicleCategoryVisible =
    isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS) &&
    sourcingType === SourcingType.CLASSIFIEDS;

  return (
    <>
      <Show when={isVehicleCategoryVisible}>
        <FilterVehicleCategory data-testid={suffixTestId('vehicleCategory', props)} />
        <Separator />
      </Show>
      <Grid columns={[1, 1, 3]}>
        <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
          <Card colorStripe="green" variant="inlineGrey">
            <VStack spacing={2}>
              <HStack spacing={2} align="center">
                <Switch
                  value={isTrue(filters.hitlistOnly)}
                  onChange={(checked) => onUpdateFilters('hitlistOnly', checked)}
                  label={i18n.t('entity.sourcing.filters.labels.hitlistOnly')}
                  data-testid={suffixTestId('hitlistOnly', props)}
                />
                <HitlistFlag />
              </HStack>
              <Box paddingLeft={12}>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.sourcing.filters.labels.hitlistOnlyDescription')}
                </Text>
              </Box>
            </VStack>
          </Card>
          <Card colorStripe="red" variant="inlineGrey">
            <VStack spacing={2}>
              <HStack spacing={2} align="center">
                <Switch
                  value={isTrue(filters.hideBlacklist)}
                  onChange={(checked) => onUpdateFilters('hideBlacklist', checked)}
                  label={i18n.t('entity.sourcing.filters.labels.hideBlacklist')}
                  data-testid={suffixTestId('hideBlacklist', props)}
                />
                <BlacklistFlag />
              </HStack>
              <Box paddingLeft={12}>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.sourcing.filters.labels.hideBlacklistDescription')}
                </Text>
              </Box>
            </VStack>
          </Card>
        </Show>
        <Show whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}>
          <Card colorStripe="orange" variant="inlineGrey">
            <VStack spacing={2}>
              <HStack spacing={2} align="center">
                <Switch
                  value={isTrue(filters.hideSuspicious)}
                  onChange={(checked) => onUpdateFilters('hideSuspicious', checked)}
                  label={i18n.t('entity.sourcing.filters.labels.hideSuspicious')}
                  data-testid={suffixTestId('hideSuspicious', props)}
                />
                <Icon value="alert/warning" color="severity.warning" size={5} />
              </HStack>
              <Box paddingLeft={12}>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.sourcing.filters.labels.hideSuspiciousDescription')}
                </Text>
              </Box>
            </VStack>
          </Card>
        </Show>
      </Grid>
      <Show
        when={
          isFeatureEnabled(featureFlags.SOURCING_HITLIST_BLACKLIST) ||
          isFeatureEnabled(featureFlags.SOURCING_SUSPICIOUS_VEHICLES)
        }
      >
        <Separator />
      </Show>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VStack spacing={4}>
          <MakesModelsSearch data-testid={suffixTestId('makesModelsSearch', props)} />
          <Grid columns={[1, 1, 3]}>
            <Box paddingHorizontal={4} maxHeight={FILTERS_GRID_MAX_HEIGHT} overflow="auto">
              <VStack spacing={2}>
                <Label>{i18n.t('page.filters.labels.topMakes')}</Label>
                <TopMakes data-testid={suffixTestId('topMakes', props)} />
                <Label>{i18n.t('page.filters.labels.makes')}</Label>
                <MakeCheckboxList data-testid={suffixTestId('makes', props)} />
              </VStack>
            </Box>
            <Box paddingHorizontal={4} maxHeight={FILTERS_GRID_MAX_HEIGHT} overflow="auto">
              <VStack spacing={2}>
                <Label>{i18n.t('page.filters.labels.models')}</Label>
                <ModelFamiliesCheckboxTree data-testid={suffixTestId('modelFamilies', props)} />
              </VStack>
            </Box>
            <Box paddingHorizontal={4} maxHeight={FILTERS_GRID_MAX_HEIGHT} overflow="auto">
              <Label tooltip={i18n.t('page.filters.labels.topSellingTooltip')}>
                {i18n.t('page.filters.labels.topSelling')}
              </Label>
              <Space vertical={4} />
              <TopSellingCheckboxList data-testid={suffixTestId('topSelling', props)} />
            </Box>
          </Grid>
        </VStack>
      </DataStatus>
    </>
  );
}
